/**
 * Shared measured geometry for authored interior furniture.
 *
 * These are model-space bounds from `gltf-transform inspect`, not guessed
 * dimensions. HQ and specialist interiors consume the same rotation/scale
 * math so placement, collision, access, and visual evidence cannot drift.
 */
export interface FurniturePlacement {
  file: string
  x: number
  y: number
  z: number
  rotY: number
  scale: number
}

export interface FurnitureAssetBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

export interface FurnitureFootprint {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

export interface FurnitureDimensions {
  width: number
  height: number
  depth: number
  bottomY: number
  topY: number
}

export const FURNITURE_ASSET_BOUNDS: Readonly<Record<string, FurnitureAssetBounds>> = {
  'kf_armchair.glb': { minX: -0.9, maxX: 0.9, minY: 0.00002, maxY: 1.22404, minZ: -0.75002, maxZ: 0.85002 },
  'kf_armchair_pillows.glb': { minX: -0.9, maxX: 0.9, minY: 0.00002, maxY: 1.22404, minZ: -0.75002, maxZ: 0.85002 },
  'kf_book_set.glb': { minX: -0.39, maxX: 0.39, minY: -0.25002, maxY: 0.25002, minZ: -0.17745, maxZ: 0.18754 },
  'kf_cabinet_medium.glb': { minX: -1, maxX: 1, minY: -0.00008, maxY: 1.00008, minZ: -0.50003, maxZ: 0.50159 },
  'kf_cactus_medium_A.glb': { minX: -0.43996, maxX: 0.43995, minY: -0.00001, maxY: 0.82726, minZ: -0.41842, maxZ: 0.41842 },
  'kf_chair_A.glb': { minX: -0.375, maxX: 0.375, minY: -0.00001, maxY: 1.25833, minZ: -0.4701, maxZ: 0.37498 },
  'kf_chair_stool.glb': { minX: -0.37498, maxX: 0.37502, minY: -0.00002, maxY: 0.50002, minZ: -0.375, maxZ: 0.375 },
  'kf_lamp_standing.glb': { minX: -0.49993, maxX: 0.49993, minY: -0.00001, maxY: 2.52, minZ: -0.49993, maxZ: 0.49993 },
  'kf_rug_rectangle_A.glb': { minX: -1.5, maxX: 1.5, minY: 0.00001, maxY: 0.09999, minZ: -1.00006, maxZ: 1.00006 },
  'kf_shelf_A_big.glb': { minX: -1, maxX: 1, minY: -0.29996, maxY: 0.09996, minZ: -0.00004, maxZ: 0.50004 },
  'kf_shelf_B_large.glb': { minX: -1, maxX: 1, minY: -0.09996, maxY: 0.29996, minZ: -0.00004, maxZ: 0.50004 },
  'kf_table_medium_long.glb': { minX: -1.5, maxX: 1.5, minY: 0.00006, maxY: 0.99994, minZ: -1.00006, maxZ: 1.00006 },
  'kf_table_small.glb': { minX: -0.5, maxX: 0.5, minY: 0, maxY: 1, minZ: -0.5, maxZ: 0.5 },
  'prop_kitchenCoffeeMachine.glb': { minX: 0, maxX: 0.18954, minY: 0, maxY: 0.17749, minZ: -0.24017, maxZ: 0 },
  'prop_laptop.glb': { minX: 0, maxX: 0.264, minY: 0, maxY: 0.1618, minZ: -0.24006, maxZ: -0.00001 },
  'prop_pottedPlant.glb': { minX: -0.10603, maxX: 0.10603, minY: 0, maxY: 0.65402, minZ: -0.12071, maxZ: 0.12071 },
  'prop_serverComputerLarge.glb': { minX: -0.64694, maxX: 0.64694, minY: 0.00035, maxY: 2.38245, minZ: -0.29365, maxZ: 0.39618 },
  'synty_office_chair_01.glb': { minX: -0.267191, maxX: 0.267191, minY: 0, maxY: 0.971596, minZ: -0.317302, maxZ: 0.317302 },
  'synty_office_coffee_machine_01.glb': { minX: -0.5193, maxX: 0.5193, minY: -0.000005, maxY: 0.816719, minZ: -0.343147, maxZ: 0.532532 },
  'synty_office_coffee_table_01.glb': { minX: -0.450916, maxX: 0.596807, minY: 0.000004, maxY: 0.424024, minZ: -0.355593, maxZ: 0.355593 },
  'synty_office_couch_03.glb': { minX: -1.145827, maxX: 1.152546, minY: 0.000076, maxY: 0.951564, minZ: -0.404199, maxZ: 0.404199 },
  'synty_office_desk_01.glb': { minX: -0.979028, maxX: 0.979027, minY: 0.000055, maxY: 0.749945, minZ: -0.442351, maxZ: 0.442351 },
  'synty_office_desk_03.glb': { minX: -1.089793, maxX: 1.089793, minY: -0.00006, maxY: 0.75006, minZ: -0.476832, maxZ: 0.476832 },
  'synty_office_desk_lamp_01.glb': { minX: -0.191073, maxX: 0.191073, minY: 0, maxY: 0.401351, minZ: -0.044355, maxZ: 0.235232 },
  'synty_office_divider_half_01.glb': { minX: -1.250027, maxX: 0.000027, minY: 0, maxY: 1.500002, minZ: 0.000005, maxZ: 0.049995 },
  'synty_office_fridge_01.glb': { minX: -0.624992, maxX: 0.624994, minY: 0, maxY: 1.958041, minZ: -0.299986, maxZ: 0.425996 },
  'synty_office_glass_door_large_01.glb': { minX: 0, maxX: 5, minY: -0.000111, maxY: 2.997112, minZ: 0.000047, maxZ: 0.173393 },
  'synty_office_kitchen_counter_01.glb': { minX: -1.000024, maxX: 0.000024, minY: -0.000018, maxY: 0.949591, minZ: 0, maxZ: 1.032959 },
  'synty_office_kitchen_counter_sink_01.glb': { minX: -1.000038, maxX: 0.000038, minY: 0, maxY: 1.466983, minZ: -0.000035, maxZ: 1.032992 },
  'synty_office_monitor_double_01.glb': { minX: -0.697906, maxX: 0.690085, minY: 0.000025, maxY: 0.835098, minZ: -0.073438, maxZ: 0.376801 },
  'synty_office_plant_08.glb': { minX: -0.152867, maxX: 0.174362, minY: 0, maxY: 0.388109, minZ: -0.167189, maxZ: 0.181502 },
  'synty_office_projector_screen_01.glb': { minX: -1.307058, maxX: 1.307058, minY: -0.00003, maxY: 1.557096, minZ: 0.000008, maxZ: 0.071809 },
  'synty_office_rug_01.glb': { minX: -1.475117, maxX: 1.475117, minY: 0.000026, maxY: 0.036687, minZ: -1.706328, maxZ: 1.706328 },
  'synty_office_server_cabinet_01_full.glb': { minX: -0.466766, maxX: 0.466766, minY: 0, maxY: 2.03399, minZ: -0.5, maxZ: 0.5 },
  'synty_office_server_cabinet_02_full.glb': { minX: -0.466766, maxX: 0.466766, minY: 0, maxY: 2.03399, minZ: -0.5, maxZ: 0.5 },
  'synty_office_shelf_01.glb': { minX: -0.571636, maxX: 0.609274, minY: 0, maxY: 1.805641, minZ: -0.238965, maxZ: 0.238965 },
  'synty_office_table_conference_01.glb': { minX: -2.027501, maxX: 2.027502, minY: 0.000029, maxY: 0.749971, minZ: -1.109195, maxZ: 1.109195 },
  'synty_office_table_round_01.glb': { minX: -0.584974, maxX: 0.584975, minY: -0.000009, maxY: 0.750009, minZ: -0.591619, maxZ: 0.59162 },
  'synty_office_whiteboard_01.glb': { minX: -0.74867, maxX: 0.74867, minY: 0.000008, maxY: 1.023338, minZ: 0.000043, maxZ: 0.056707 },
}

/** Rotation- and scale-aware world-XZ AABB for one placement. */
export function furnitureFootprint(
  placement: FurniturePlacement,
  boundsByFile: Readonly<Record<string, FurnitureAssetBounds>> = FURNITURE_ASSET_BOUNDS,
): FurnitureFootprint | undefined {
  const bounds = boundsByFile[placement.file]
  if (!bounds) return undefined
  const radians = placement.rotY * Math.PI / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const points = [
    [bounds.minX, bounds.minZ],
    [bounds.minX, bounds.maxZ],
    [bounds.maxX, bounds.minZ],
    [bounds.maxX, bounds.maxZ],
  ].map(([localX, localZ]) => ({
    x: placement.x + (localX * cos + localZ * sin) * placement.scale,
    z: placement.z + (-localX * sin + localZ * cos) * placement.scale,
  }))
  return {
    minX: Math.min(...points.map((point) => point.x)),
    maxX: Math.max(...points.map((point) => point.x)),
    minZ: Math.min(...points.map((point) => point.z)),
    maxZ: Math.max(...points.map((point) => point.z)),
  }
}

/** Measured world dimensions, including rotation, placement height, and scale. */
export function furnitureDimensions(
  placement: FurniturePlacement,
  boundsByFile: Readonly<Record<string, FurnitureAssetBounds>> = FURNITURE_ASSET_BOUNDS,
): FurnitureDimensions | undefined {
  const bounds = boundsByFile[placement.file]
  const footprint = furnitureFootprint(placement, boundsByFile)
  if (!bounds || !footprint) return undefined
  return {
    width: footprint.maxX - footprint.minX,
    height: (bounds.maxY - bounds.minY) * placement.scale,
    depth: footprint.maxZ - footprint.minZ,
    bottomY: placement.y + bounds.minY * placement.scale,
    topY: placement.y + bounds.maxY * placement.scale,
  }
}

export function furnitureFootprintsOverlap(
  first: FurnitureFootprint,
  second: FurnitureFootprint,
  epsilon = 1e-4,
): boolean {
  return Math.min(first.maxX, second.maxX) - Math.max(first.minX, second.minX) > epsilon
    && Math.min(first.maxZ, second.maxZ) - Math.max(first.minZ, second.minZ) > epsilon
}
